import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModuleRenderer } from '../../components/features/listings/ModuleRenderer/ModuleRenderer';
import { ListingHeaderActions } from '../../components/features/listings/ListingHeaderActions';
import { SpokeFooterCTAs } from '../../components/features/listings/SpokeFooterCTAs';
import { PhotoGallerySection } from '../../components/features/listings/PhotoGallery';
import { PageFooter } from '../../components/features/listings/PageFooter';
import { DetailPanelOutlet, DetailPanelProvider } from '../../components/ui/DetailPanel';
import { CloseButton } from '../../components/ui/CloseButton';
import { useScrollFadeHeader } from './hooks/useScrollFadeHeader';
import { ExpandedContainer } from '../../layouts/ExpandedContainer';
import { DesktopStickyHeader } from './DesktopStickyHeader';
import { HaloListingHead } from './HaloListingHead';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import { emitTrackViewHdpEvent } from 'app/shared/models/Clickstream/PageViewEvents';
import { registerClientProfilerPage } from 'app/shared/utils/clientProfiler';
import { trackListingView } from 'app/client/tracking';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { clearBdpScroll, hasBdpScrollFor } from 'app/shared/modules/hdp/BuildingHdp/bdpScrollRestore';
import { clearSrpScrollPosition } from 'app/shared/hooks/useSrpScrollRestore';
import * as styles from './haloListingPage.styles';

export const HaloListingPage = () => {
  const dispatch = useDispatch();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const isClientSideNav = useSelector((state: RootReduxState) => state.app.isClientSideLoadedPage);
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const listingDetails = listing as unknown as ListingDetails | undefined;
  const isUnit = listingDetails?.isUnit ?? false;
  const uriBuilding = listingDetails?.uriBuilding;
  // Only navigate back to BDP if this was a client-side navigation from a BDP (not a direct page load/SSR)
  const [cameFromBdp, setCameFromBdp] = useState(false);
  useEffect(() => {
    if (isClientSideNav) {
      setCameFromBdp(isUnit && hasBdpScrollFor(uriBuilding));
    } else {
      // SSR direct load: clear stale scroll data so SRP starts at top
      clearBdpScroll();
      clearSrpScrollPosition();
    }
  }, [isUnit, uriBuilding, isClientSideNav]);
  useEffect(() => {
    emitTrackViewHdpEvent({ triggerLocation: 'home_details_halo' });
    registerClientProfilerPage('hotpads-hdp-halo');
    if (listing) {
      trackListingView(listing as unknown as ListingDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { backgroundOpacity } = useScrollFadeHeader({
    enabled: true,
    scrollContainerRef: isMobile ? undefined : mainContentRef,
    threshold: 50,
    useWindowScroll: isMobile,
  });

  useEffect(() => {
    if (!isMobile) return;

    const savedStyles: Array<{ el: HTMLElement; overflow: string; height: string }> = [];

    const splitMapTemplate = document.querySelector('.SplitMapTemplate');
    if (splitMapTemplate instanceof HTMLElement) {
      savedStyles.push({
        el: splitMapTemplate,
        overflow: splitMapTemplate.style.overflow,
        height: splitMapTemplate.style.height,
      });
      splitMapTemplate.style.overflow = 'visible';
      splitMapTemplate.style.height = 'auto';
    }

    const splitMapSidebar = document.getElementById('SplitMapTemplate-right-sidebar');
    if (splitMapSidebar) {
      savedStyles.push({
        el: splitMapSidebar,
        overflow: splitMapSidebar.style.overflow,
        height: splitMapSidebar.style.height,
      });
      splitMapSidebar.style.overflow = 'visible';
      splitMapSidebar.style.height = 'auto';
    }

    return () => {
      savedStyles.forEach(({ el, overflow, height }) => {
        el.style.overflow = overflow;
        el.style.height = height;
      });
    };
  }, [isMobile]);

  // Anchor href for SEO (no side effects during render). onClick does the full dispatch.
  let backHref = '';
  if (cameFromBdp) {
    backHref = uriBuilding ?? '';
  } else {
    try {
      backHref = dispatch(RouteActions.getAreaPath()) as unknown as string;
    } catch {
      backHref = '/';
    }
  }

  const navigateBack = () => {
    if (cameFromBdp) {
      const buildingUrl = dispatch(
        RouteActions.buildListingPathWithMap({ listingUri: uriBuilding, keepMapLocation: true, filter: undefined }),
      ) as unknown as string;
      window.router.transitionTo(buildingUrl);
    } else if (isClientSideNav) {
      clearBdpScroll();
      const backUrl = dispatch(RouteActions.getAreaPathWithMapData()) as unknown as string;
      window.router.transitionTo(backUrl);
    } else {
      // SSR direct load: full page nav so listings refresh for the wider area
      clearBdpScroll();
      window.location.href = backHref;
    }
  };

  // Desktop anchor onClick: preventDefault + client-side nav (SSR falls through to <a> href)
  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (cameFromBdp || isClientSideNav) {
      e.preventDefault();
    }
    navigateBack();
  };

  return (
    <ExpandedContainer>
      <HaloListingHead />
      <DetailPanelProvider
        isMobile={isMobile}
        scrollContainerRef={mainContentRef}
        closeLabel={cameFromBdp ? 'Back to building' : undefined}
        closeLabelShort={cameFromBdp ? 'Building' : undefined}
      >
        <ExpandedContent
          isMobile={isMobile}
          mainContentRef={mainContentRef}
          backgroundOpacity={backgroundOpacity}
          backHref={backHref}
          onBackClick={handleBackClick}
          backLabel={cameFromBdp ? 'Back to building' : undefined}
        />
      </DetailPanelProvider>
    </ExpandedContainer>
  );
};

type ExpandedContentProps = {
  isMobile: boolean;
  mainContentRef: React.RefObject<HTMLDivElement | null>;
  backgroundOpacity: number;
  backHref: string;
  onBackClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  backLabel?: string;
};

const ExpandedContent = ({
  isMobile,
  mainContentRef,
  backgroundOpacity,
  backHref,
  onBackClick,
  backLabel,
}: ExpandedContentProps) => (
  <div className={styles.detailHubContainer}>
    <header
      className={styles.detailHeader}
      style={{
        backgroundColor: `rgba(255, 255, 255, ${backgroundOpacity})`,
        borderBottom: backgroundOpacity > 0 ? `1px solid rgba(216, 216, 216, ${backgroundOpacity})` : 'none',
      }}
    >
      <CloseButton
        variant="floating"
        className={styles.expandedCloseButton}
        href={backHref}
        onClick={onBackClick}
        aria-label={backLabel ?? 'Back to search results'}
      />
      <div />
      <div className={styles.headerActions}>
        <ListingHeaderActions />
      </div>
    </header>

    <main ref={mainContentRef as React.Ref<HTMLDivElement>} className={styles.detailMainContent}>
      <DesktopStickyHeader backHref={backHref} onBackClick={onBackClick} backLabel={backLabel} />
      <div className={styles.photoGalleryPadding}>
        <PhotoGallerySection />
      </div>
      <div className={styles.detailMainContentPadding}>
        <ModuleRenderer />
        <PageFooter />
      </div>
    </main>

    {!isMobile && <DetailPanelOutlet headerActions={<ListingHeaderActions size="sm" showCTAs />} />}

    <StickyFooter />
  </div>
);

const StickyFooter = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  if (!listing || listing.active === false) return null;

  return (
    <footer className={styles.detailFooter}>
      <div className={styles.spokeFooterActions}>
        <SpokeFooterCTAs />
      </div>
    </footer>
  );
};

export default HaloListingPage;
